/**
 * La situation financière d'UNE famille — le second clic.
 *
 * ## Ce qu'elle ne réécrit pas
 *
 * `CompteDeLaFamille` existe déjà et affiche factures, notes de crédit
 * nommées et cliquables, versements, virements encore en file et avoir. Il est
 * monté à l'identique par trois écrans du rapprochement. Cette page lui ajoute
 * les totaux de la famille, et rien d'autre : un second panneau financier
 * finirait par dire autre chose que le premier.
 *
 * ## Les mêmes chiffres que la liste
 *
 * `loadFamily()` lit la MÊME vue, avec les MÊMES colonnes et la MÊME
 * projection que `loadFamilies()`. C'est ce qui garantit qu'une famille ne
 * montre pas ici un reste dû différent de celui de la liste.
 */

import { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { AlertCircle, ArrowLeft, Loader2 } from 'lucide-react';
import { loadFamily } from '../../lib/finances/gateway.supabase';
import {
  detteExploitable, ecartAncienCircuit, euros, libelle, nomFamille, retard,
  type FamilySituation,
} from '../../lib/finances/situation';
import { messageErreur } from '../../lib/payments/erreur';
import CompteDeLaFamille from './components/CompteDeLaFamille';
import { ficheUtilisateur } from '../../lib/navigation/chemins';

export default function AdminUserFinancesPage() {
  const { userId = '' } = useParams();
  const [famille, setFamille] = useState<FamilySituation | null>(null);
  const [chargement, setChargement] = useState(true);
  const [erreur, setErreur] = useState<string | null>(null);

  const charger = useCallback(async () => {
    setChargement(true);
    setErreur(null);
    try {
      setFamille(await loadFamily(userId));
    } catch (e) {
      setErreur(messageErreur(e, 'Situation illisible'));
    } finally {
      setChargement(false);
    }
  }, [userId]);

  useEffect(() => { void charger(); }, [charger]);

  const etat = famille ? libelle(famille.situation) : null;
  const ecart = famille ? ecartAncienCircuit(famille) : null;

  return (
    <div className="space-y-6">
      <Link
        to={ficheUtilisateur(userId)}
        className="inline-flex items-center gap-1.5 text-sm text-gray-600 hover:text-gray-900"
      >
        <ArrowLeft className="h-4 w-4" /> Fiche du compte
      </Link>

      <div>
        <h1 className="text-2xl font-bold">
          Situation financière {famille ? `— ${nomFamille(famille)}` : ''}
        </h1>
        <p className="text-gray-600">
          Ce que cette famille a été facturée, a payé, et doit encore.
        </p>
      </div>

      {chargement && (
        <p className="flex items-center gap-2 text-sm text-gray-600">
          <Loader2 className="h-4 w-4 animate-spin" /> Lecture de la situation…
        </p>
      )}

      {erreur && (
        <p className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800">
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
          {erreur}
        </p>
      )}

      {famille && (
        <>
          {/*
            L'avertissement d'abord, quand il vaut pour CETTE famille : son
            reste dû est surévalué, et un rappel lui réclamerait de l'argent
            déjà reçu.
          */}
          {ecart && ecart.ecartCents > 0 && (
            <p className="rounded-xl border border-amber-300 bg-amber-50 p-4 text-sm text-amber-900">
              <strong>Payé selon l'ancien circuit.</strong> {euros(ecart.ecartCents)} sur
              {' '}{ecart.factures} facture{ecart.factures > 1 ? 's' : ''} sont connus payés par
              l'ancien circuit sans que le nouveau modèle le sache. Le reste dû ci-dessous est
              donc surévalué d'autant.
            </p>
          )}

          <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
            {/* `detteExploitable` rend un BOOLÉEN : elle dit si le reste dû est
                réclamable, pas combien il vaut. Le montant reste `outstandingCents`,
                et la mention le qualifie. */}
            <Chiffre libelle="Reste dû" valeur={euros(famille.outstandingCents)}
                     detail={detteExploitable(famille)
                       ? (famille.daysOverdue > 0 ? `en retard de ${retard(famille.daysOverdue)}` : 'réclamable')
                       : 'à ne pas réclamer en l\u2019état'} />
            <Chiffre libelle="Facturé net" valeur={euros(famille.netCents)}
                     detail={`${famille.invoiceCount} facture${famille.invoiceCount > 1 ? 's' : ''}`} />
            <Chiffre libelle="Reçu" valeur={euros(famille.receivedCents)} detail="nouveau modèle" />
            <Chiffre libelle="Provision" valeur={euros(famille.provisionCents)}
                     detail={etat ? etat.label : ''} />
          </div>

          {/* Le compte complet, tel que les écrans du rapprochement le montrent. */}
          <CompteDeLaFamille userId={userId} />
        </>
      )}
    </div>
  );
}

function Chiffre({ libelle: l, valeur, detail }: { libelle: string; valeur: string; detail: string }) {
  return (
    <div className="rounded-xl border border-gray-200 bg-white p-4">
      <p className="text-xs font-medium uppercase tracking-wide text-gray-500">{l}</p>
      <p className="mt-1 text-xl font-bold tabular-nums text-gray-900">{valeur}</p>
      {detail && <p className="mt-0.5 text-xs text-gray-500">{detail}</p>}
    </div>
  );
}
